import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { updateSearch } from 'bernie-core';

import { DynamicSearchMap } from '@shared-ui/lodging-property-search';
import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { PROPERTY_LISTING_BATCH_KEY } from 'src/common/constants/batchKeys';

import { calculateNewMapInputs } from './map-utils';
import { checkPropertyClusterLabelKey } from './eg-maps-utils';

import { DestinationInput, LodgingPwaPropertySearchQueryVariables, PaginationInput } from 'src/common/__generated__/api';
import { EGMapConfiguration, FormatFunction } from '@eg-maps/core';

interface DynamicSearchMapWrapperProps {
  context?: ContextStore;
  searchCriteria?: SearchCriteriaModel;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  cfg?: EGMapConfiguration;
  clientPadding?: string;
  onMapDialogClosed?: () => void;
  filterComponent?: React.ReactNode;
  triggerRef?: React.Ref<HTMLElement>;
}

type DynamicSearchMapInputs = Omit<LodgingPwaPropertySearchQueryVariables, 'context'>;

export const DynamicSearchMapWrapper: React.FC<DynamicSearchMapWrapperProps> = inject(
  'context',
  'searchCriteria',
  'multiItem',
  'searchResults',
  'experiment',
  'analytics',
  'currentHotel'
)(
  observer(
    ({
      context,
      searchCriteria,
      multiItem,
      searchResults,
      experiment,
      analytics,
      currentHotel,
      cfg,
      clientPadding,
      onMapDialogClosed,
      filterComponent,
      triggerRef,
    }) => {
      const history = useHistory();
      const location = useLocation();
      const l10n = useLocalization();

      if (!context || !searchResults?.searchCriteria) return null;

      const calculateMapInputs = React.useCallback(() => {
        const useSharedUIListingsQuery =
          !multiItem?.isPackageShoppingPath() && experiment?.exposures?.EG_Property_Search_Query_Migration?.bucket === 1;
        const paginationPreviousSubset = useSharedUIListingsQuery
          ? searchCriteria?.paginationSubSets?.previousSubset
          : searchResults?.pagination?.subSets?.previousSubset;
        const isReducedListingExperiment = experiment?.exposures?.lodging_search_results_reduced_listings_feature_toggle?.bucket === 1;
        const paginationInput: PaginationInput = {
          // explicitly pass resultsSize=100 on map when TnL 47843 reduces listings size
          size: isReducedListingExperiment ? 100 : paginationPreviousSubset?.size,
          startingIndex: (paginationPreviousSubset?.startingIndex ?? 0) + (paginationPreviousSubset?.size ?? 0),
        };

        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          context: bexApiContext,
          ...rest
        } = getPropertySearchInput(context, searchResults.searchCriteria, multiItem?.multiItemContext, paginationInput);

        return rest;
      }, [context, multiItem, searchResults, searchCriteria, experiment]);

      const [mapInputs, setMapInputs] = React.useState<DynamicSearchMapInputs>(calculateMapInputs());

      React.useEffect(() => {
        autorun(() => {
          const newState = calculateMapInputs();
          setMapInputs((prevState: DynamicSearchMapInputs) => calculateNewMapInputs(prevState, newState));
        });
      }, [context, multiItem, searchResults, searchCriteria, experiment]);

      const onDestinationChanged = (destination: DestinationInput) => {
        const latLong = destination.coordinates
          ? `${destination.coordinates.latitude},${destination.coordinates.longitude}`
          : 'undefined,undefined';
        const mapBounds = destination.mapBounds
          ? [
              [destination.mapBounds[0]?.latitude, destination.mapBounds[0]?.longitude],
              [destination.mapBounds[1]?.latitude, destination.mapBounds[1]?.longitude],
            ]
          : null;

        if (analytics?.clickTracker) {
          analytics.clickTracker('Module.Map.Search.Button', 'User clicking the map search button on PWA HSR Map', null, () => null);
        }

        updateSearch({
          history,
          location,
          newParams: {
            regionId: '',
            destination: '',
            selected: '',
            poi: '',
            latLong,
            // developer note, since this bernie function only accepts strings we would need to keep the ts-ignore here
            // to resolve type issues when querying with the mapBounds
            // @ts-ignore
            mapBounds,
            neighborhood: '',
          },
        });
        window.scrollTo(0, 0);
      };

      const translate: FormatFunction = React.useCallback((key, args) => checkPropertyClusterLabelKey(key, l10n, ...args), [l10n]);

      const egMapsConfig: EGMapConfiguration = React.useMemo(
        () =>
          ({
            ...cfg,
            i18n: {
              formatFunction: translate,
            },
          } as EGMapConfiguration),
        [cfg, translate]
      );

      const fullscreenOptions = onMapDialogClosed
        ? {
            onClose: onMapDialogClosed,
            isDialogOpen: true,
            triggerRef: triggerRef ?? null,
          }
        : undefined;

      const batchKey = experiment?.exposures?.SHOPPING_PWA_BATCHING_SRP_QUERY?.bucket === 1 ? PROPERTY_LISTING_BATCH_KEY : undefined;

      return (
        <DynamicSearchMap
          batchKey={batchKey}
          inputs={mapInputs}
          selectedFeatureId={searchCriteria?.getHighlightedPropertyId}
          clientPadding={clientPadding}
          fullscreenOptions={fullscreenOptions}
          egMapsConfig={egMapsConfig}
          filterComponent={filterComponent}
          onDestinationChanged={onDestinationChanged}
          onLodgingCardClickCallback={propertyId => {
            currentHotel?.setPropertyListingIdAsCurrentHotel(propertyId as string);
          }}
          height="100%"
        />
      );
    }
  )
);

DynamicSearchMapWrapper.displayName = 'DynamicSearchMapWrapper';
